import React from 'react';
import { BarChart, Bar, ResponsiveContainer, Cell } from 'recharts';
import { DashboardMonthData } from '../../hooks/useCaapApi';

interface StepCardProps {
  stepNumber: number;
  stepName: string;
  months: DashboardMonthData[];
}

const STEP_LABELS: Record<number, { badge: string; badgeColor: string }> = {
  1: { badge: 'Write Off', badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
  2: { badge: 'Report Only', badgeColor: 'bg-surface-hover text-muted' },
  3: { badge: 'Write Off', badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
  4: { badge: 'Write Off', badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
  5: { badge: 'Write Off', badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
  6: { badge: 'Write Off', badgeColor: 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300' },
};

function formatCurrency(value: number): string {
  if (value >= 1000) {
    return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  }
  return '$' + value.toLocaleString('en-US', { minimumFractionDigits: 2 });
}

export const StepCard: React.FC<StepCardProps> = ({ stepNumber, stepName, months }) => {
  const isReportOnly = stepNumber === 2;
  const current = months[months.length - 1];
  const currentAccounts = current?.accountCount ?? 0;
  const currentPayoff = current?.payoffBalance ?? 0;
  const { badge, badgeColor } = STEP_LABELS[stepNumber] ?? { badge: '', badgeColor: '' };

  const chartData = months.map((m) => ({
    accounts: m.accountCount,
  }));

  return (
    <div
      className={`rounded-lg shadow-sm border p-5 ${
        isReportOnly
          ? 'bg-surface border-border-default opacity-75'
          : 'bg-surface-alt border-border-default'
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <span className="text-xs font-semibold text-muted">
            STEP {stepNumber}
          </span>
          <h3 className={`text-sm font-semibold ${isReportOnly ? 'text-secondary' : 'text-primary'}`}>
            {stepName}
          </h3>
        </div>
        {badge && (
          <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded ${badgeColor}`}>
            {badge}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-3">
        <div>
          <p className="text-xs text-muted">Accounts</p>
          <p className={`text-xl font-bold ${isReportOnly ? 'text-secondary' : 'text-primary'}`}>
            {currentAccounts > 0 ? currentAccounts : '\u2014'}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted">Payoff Balance</p>
          <p className={`text-xl font-bold ${isReportOnly ? 'text-secondary' : 'text-primary'}`}>
            {currentPayoff > 0 ? formatCurrency(currentPayoff) : '\u2014'}
          </p>
        </div>
      </div>

      <div className="h-12">
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Bar dataKey="accounts" radius={[1, 1, 0, 0]}>
                {chartData.map((_, index) => (
                  <Cell
                    key={index}
                    fill={
                      isReportOnly
                        ? index === chartData.length - 1 ? '#9ca3af' : '#d1d5db'
                        : index === chartData.length - 1 ? '#6b7280' : '#d1d5db'
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full flex items-center justify-center text-muted text-xs">
            No data yet
          </div>
        )}
      </div>
    </div>
  );
};
